import { memo, useState } from "react";
import styled from "styled-components";
import { Tooltip } from "antd";
import { formatDateTime, formatDateTimeSeconds, formatRelativeTime } from "~/utils/dateUtils";

/**
 * Props for DateTimeViewer component
 * 
 * @example
 * ```tsx
 * <DateTimeViewer date={new Date()} showSeconds={true} />
 * <DateTimeViewer date="2024-01-15" initialMode="relative" />
 * ```
 */
export interface DateTimeViewerProps {
  date?: string | number | Date | null;
  showSeconds?: boolean;
  initialMode?: 'formatted' | 'relative';
  className?: string;
  showTooltip?: boolean;
  /** Visual weight: 'primary' = normal text color, 'secondary' = muted/dim */
  severity?: 'primary' | 'secondary';
}

/**
 * DateTimeViewer - Interactive date/time display component
 * 
 * Displays dates in two toggleable modes:
 * - Formatted: DD/MM/YYYY HH:mm (or HH:mm:ss if showSeconds=true)
 * - Relative: "5 phút trước", "2 ngày trước"
 * 
 * Click to toggle between modes. Hover to see alternate format in tooltip.
 * 
 * @example
 * ```tsx
 * <DateTimeViewer date={user.createdAt} />
 * <DateTimeViewer date={comment.updatedAt} initialMode="relative" showSeconds />
 * ```
 */
const DateTimeViewer = (props: DateTimeViewerProps) => {
  const {
    date,
    showSeconds = false,
    initialMode = 'formatted',
    className,
    showTooltip = true,
    severity = 'primary',
  } = props;

  const [mode, setMode] = useState<'formatted' | 'relative'>(initialMode);

  if (!date) return <span className={className}>-</span>;

  const formattedText = showSeconds 
    ? formatDateTimeSeconds(date) 
    : formatDateTime(date);
  const relativeText = formatRelativeTime(date);

  if (formattedText === '-' || relativeText === '-') {
    return <span className={className}>-</span>;
  }

  const displayText = mode === 'formatted' ? formattedText : relativeText;
  const tooltipText = mode === 'formatted' ? relativeText : formattedText;

  const handleClick = () => {
    setMode(prev => prev === 'formatted' ? 'relative' : 'formatted');
  };

  const content = (
    <UI_Wrapper className={className} onClick={handleClick} $severity={severity}>
      {displayText}
    </UI_Wrapper>
  );

  if (!showTooltip) {
    return content;
  }

  return (
    <Tooltip title={tooltipText}>
      {content}
    </Tooltip>
  );
};

export default memo(DateTimeViewer);

const UI_Wrapper = styled.span<{ $severity: 'primary' | 'secondary' }>`
  display: inline-block;
  cursor: pointer;
  user-select: none;
  padding: 2px 6px;
  border-radius: 4px;
  transition: all 0.2s ease;
  color: ${({ $severity }) => $severity === 'secondary' ? '#999' : 'inherit'};
  font-size: ${({ $severity }) => $severity === 'secondary' ? '0.9em' : 'inherit'};

  &:hover {
    background-color: #f0f0f0;
    color: #1890ff;
  }
`;
